/* StatusRenderer.js */

"use strict";

//const _ = require('lodash');

class StatusColumnRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("status-column");
        this._rootElem.component = this._model;

        this._cleanIcon = document.createElement("base-icon-gl");
        this._cleanIcon.dataset.icon = "common.success";
        this._cleanIcon.setAttribute("title", "Successful");

        this._pausedIcon = document.createElement("base-icon-gl");
        this._pausedIcon.dataset.icon = "common.paused";
        this._pausedIcon.setAttribute("title", "Paused");

        this._resumedIcon = document.createElement("base-icon-gl");
        this._resumedIcon.dataset.icon = "common.pending";
        this._resumedIcon.setAttribute("title", "Resumed");

        this._failedIcon = document.createElement("base-icon-gl");
        this._failedIcon.dataset.icon = "common.failed";
        this._failedIcon.setAttribute("title", "Failed");
        this._failedIcon.dataset.invalid = "true";

        this._startedIcon = document.createElement("base-icon-gl");
        this._startedIcon.dataset.icon = "configdrift.started";
        this._startedIcon.setAttribute("title", "Started");

        this._runningIcon = document.createElement("base-icon-gl");
        this._runningIcon.dataset.icon = "configdrift.started";
        this._runningIcon.setAttribute("title", "Running");

        this._completedIcon = document.createElement("base-icon-gl");
        this._completedIcon.dataset.icon = "common.completed";
        this._completedIcon.setAttribute("title", "Completed");

        this._successfulIcon = document.createElement("base-icon-gl");
        this._successfulIcon.dataset.icon = "common.success";
        this._successfulIcon.setAttribute("title", "Successful");

        this._skippedIcon = document.createElement("base-icon-gl");
        this._skippedIcon.dataset.icon = "common.skipped";
        this._skippedIcon.setAttribute("title", "Skipped");

        this._overlappedIcon = document.createElement("base-icon-gl");
        this._overlappedIcon.dataset.icon = "worktrigger.overlapped";
        this._overlappedIcon.setAttribute("title", "Overlapped");

        this._missedIcon = document.createElement("base-icon-gl");
        this._missedIcon.dataset.icon = "common.error";
        this._missedIcon.setAttribute("title", "Missed");
        this._missedIcon.dataset.invalid = "true";

        this._queuedIcon = document.createElement("base-icon-gl");
        this._queuedIcon.dataset.icon = "configdrift.queued";
        this._queuedIcon.setAttribute("title", "Queued");

        this._rootElem.appendChild(this._cleanIcon);
        this._rootElem.appendChild(this._pausedIcon);
        this._rootElem.appendChild(this._resumedIcon);
        this._rootElem.appendChild(this._failedIcon);
        this._rootElem.appendChild(this._runningIcon);
        this._rootElem.appendChild(this._startedIcon);
        this._rootElem.appendChild(this._completedIcon);
        this._rootElem.appendChild(this._successfulIcon);
        this._rootElem.appendChild(this._skippedIcon);
        this._rootElem.appendChild(this._missedIcon);
        this._rootElem.appendChild(this._overlappedIcon);
        this._rootElem.appendChild(this._queuedIcon);

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._cleanIcon.remove();
            this._cleanIcon = undefined;
            this._failedIcon.remove();
            this._failedIcon = undefined;
            this._startedIcon.remove();
            this._startedIcon = undefined;
            this._runningIcon.remove();
            this._runningIcon = undefined;
            this._completedIcon.remove();
            this._completedIcon = undefined;
            this._successfulIcon.remove();
            this._successfulIcon = undefined;
            this._skippedIcon.remove();
            this._skippedIcon = undefined;
            this._missedIcon.remove();
            this._missedIcon = undefined;
            this._overlappedIcon.remove();
            this._overlappedIcon = undefined;
            this._queuedIcon.remove();
            this._queuedIcon = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Clean", (this._onClean = this._onClean.bind(this)));
        this._model.on("Failed", (this._onFailed = this._onFailed.bind(this)));
        this._model.on("Running", (this._onRunning = this._onRunning.bind(this)));
    }
    _unregisterListeners() {
        this._model.removeAllListeners("Clean");
        this._model.removeAllListeners("Failed");
        this._model.removeAllListeners("Running");
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._setActiveIcon(this._model.getValue());
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onClean(newVal) {
        this._setClean(newVal);
    }

    _onFailed(newVal) {
        this._setFailed(newVal);
    }

    _onRunning(newVal) {
        this._setRunning(newVal);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _setActiveIcon(sts) {
        this._cleanIcon.classList.add("hidden");
        this._pausedIcon.classList.add("hidden");
        this._resumedIcon.classList.add("hidden");
        this._failedIcon.classList.add("hidden");
        this._runningIcon.classList.add("hidden");
        this._queuedIcon.classList.add("hidden");
        this._startedIcon.classList.add("hidden");
        this._completedIcon.classList.add("hidden");
        this._successfulIcon.classList.add("hidden");
        this._skippedIcon.classList.add("hidden");
        this._missedIcon.classList.add("hidden");
        this._overlappedIcon.classList.add("hidden");
        switch (sts) {
            case "clean":
                this._cleanIcon.classList.remove("hidden");
                break;
            case "paused":
                this._pausedIcon.classList.remove("hidden");
                break;
            case "running":
                this._runningIcon.classList.remove("hidden");
                break;
            case "queued":
                this._queuedIcon.classList.remove("hidden");
                break;
            case "started":
                this._startedIcon.classList.remove("hidden");
                break;
            case "resumed":
                this._resumedIcon.classList.remove("hidden");
                break;
            case "failed":
                this._failedIcon.classList.remove("hidden");
                break;
            case "completed":
                this._completedIcon.classList.remove("hidden");
                break;
            case "successful":
                this._successfulIcon.classList.remove("hidden");
                break;
            case "skipped":
                this._skippedIcon.classList.remove("hidden");
                break;
            case "missed":
                this._missedIcon.classList.remove("hidden");
                break;
            case "overlapped":
                this._overlappedIcon.classList.remove("hidden");
                break;
        }
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = StatusColumnRenderer;
